<template>
    <div class="contact-page">
        <!-- 1. 顶部主视觉 (Hero Section) -->
        <section class="contact-hero">
            <div class="banner">
                <!-- 背景图片 -->
                <img src="https://qiniu-au.wifigsmalarm.com/images/page-banner.png" class="background-img" alt="背景图">

                <!-- 文字内容容器 -->
                <div class="text-content">
                    <h1>{{ t('contact.hero.title') }}</h1>
                    <p>{{ t('contact.hero.desc') }}</p>
                </div>
            </div>
        </section>

        <transition name="fade">
            <div class="modal-overlay" v-if="isMessageModalOpen" @click="closeMessageModal">
                <div class="modal-content message-modal" @click.stop>
                    <div class="message-icon" :class="messageType">
                        <!-- 成功图标 -->
                        <svg v-if="messageType === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <!-- 警告图标 -->
                        <svg v-if="messageType === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                    <h3 class="message-title">{{ messageTitle }}</h3>
                    <p class="message-text">{{ messageText }}</p>
                    <button class="btn-primary btn-block" @click="closeMessageModal">
                        {{ $t('product.alert.confirm') || 'OK' }}
                    </button>
                </div>
            </div>
        </transition>

        <!-- 3. 留言表单与办公地址 (Form & Locations) -->
        <section class="form-location-section bg-light">
            <div class="container split-layout">

                <!-- 左侧：在线留言表单 -->
                <div class="form-container" id="contact-form">
                    <h2>{{ t('contact.form.title') }}</h2>
                    <p class="form-subtitle">{{ t('contact.form.subtitle') }}</p>

                    <form @submit.prevent="submitForm" class="contact-form">
                        <!-- 伪装蜜罐字段（机器人遍历表单常会自动填充常见 input 名称，对正常用户完全不可见且不抢焦点） -->
                        <div style="opacity: 0; position: absolute; top: -9999px; left: -9999px; height: 0; width: 0; pointer-events: none;"
                            aria-hidden="true">
                            <input type="text" name="company_website" v-model="honeypot" tabindex="-1"
                                autocomplete="off" />
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label>{{ $t('product.form.name') }} </label>
                                <input type="text" v-model="formData.name"
                                    :placeholder="$t('product.form.namePlaceholder')" />
                            </div>
                        </div>

                        <div class="form-row">

                            <div class="form-group">
                                <label for="email">{{ t('contact.form.email') }}</label>
                                <input type="email" id="email" v-model="formData.email" required
                                    :placeholder="t('contact.form.emailPlaceholder')" />
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group">
                                <label for="whasapp">{{ t('product.form.whatsapp') }}</label>
                                <input type="text" id="whatsapp" v-model="formData.whatsapp" required
                                    :placeholder="t('product.form.whatsappPlaceholder')" />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="inquiryType">{{ t('contact.form.inquiryType') }}</label>
                                <div class="select-wrapper">
                                    <select id="inquiryType" v-model="formData.order_type" required>
                                        <option value="" disabled>{{ t('contact.form.selectTopic') }}</option>
                                        <option v-for="type in inquiryTypes" :key="type.value" :value="type.value">
                                            {{ type.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="message">{{ t('contact.form.message') }}</label>
                                <textarea id="message" v-model="formData.remark" rows="5" required
                                    :placeholder="t('contact.form.messagePlaceholder')"></textarea>
                            </div>
                        </div>

                        <!-- Cloudflare Turnstile 验证组件 -->
                        <div class="form-turnstile">
                            <TurnstileWidget ref="turnstileRef" :sitekey="TURNSTILE_SITE_KEY"
                                @verify="onTurnstileSuccess" @expire="turnstileToken = ''"
                                @error="turnstileToken = ''" />
                        </div>

                        <button type="submit" class="btn-submit" :disabled="isSubmitting">
                            {{ isSubmitting ? t('contact.form.sending') : t('contact.form.submitBtn') }}
                        </button>

                        <!-- 提交成功提示 -->
                        <transition name="fade">
                            <div v-if="submitSuccess" class="success-message">
                                ✅ {{ t('contact.form.successMsg') }}
                            </div>
                        </transition>
                    </form>
                </div>

                <!-- 右侧：全球办公地点 -->
                <div class="locations-container">
                    <h2>{{ t('contact.location.title') }}</h2>

                    <div class="office-card">
                        <h4>{{ t('contact.location.officeName') }}</h4>
                        <p>{{ t('contact.location.address') }}</p>
                        <p class="office-phone">📞 {{ t('contact.location.phone') }}</p>
                    </div>
                </div>

            </div>
        </section>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { Order } from '@/api/product'
import TurnstileWidget from '@/components/TurnstileWidget.vue'

const { t } = useI18n()
const route = useRoute()

// --- 安全防护：Turnstile 与 蜜罐 ---
const turnstileRef = ref(null)
const turnstileToken = ref('')
const honeypot = ref('')
const lastSubmitTime = ref(0)
const submitCooldown = 5000 // 限制每次提交间隔至少 5 秒
const TURNSTILE_SITE_KEY = '0x4AAAAAAFVXUplqQ584Lc_l'

const isMessageModalOpen = ref(false)
const messageType = ref('success') // 'success' 或 'warning'
const messageTitle = ref('')
const messageText = ref('')

const showMessage = (type, title, text) => {
    messageType.value = type
    messageTitle.value = title
    messageText.value = text
    isMessageModalOpen.value = true
}

const closeMessageModal = () => {
    isMessageModalOpen.value = false
}


const onTurnstileSuccess = (token) => {
    turnstileToken.value = token
}

const resetSecurityState = () => {
    turnstileToken.value = ''
    honeypot.value = ''
    if (turnstileRef.value && typeof turnstileRef.value.reset === 'function') {
        turnstileRef.value.reset()
    }
}

// SQL 注入与危险字符特征正则 (匹配单引号闭合、联合查询、注释符号、脚本标签等)
const SQLI_REGEX = /('|"|;|--|\/\*|\*\/|union\s+select|select\s+.*from|insert\s+into|delete\s+from|drop\s+table|update\s+.*set|<script[\s\S]*?>)/i

// 基础文本过滤：剔除前后空白、危险闭合字符
const sanitizeInput = (str) => {
    if (typeof str !== 'string') return ''
    return str.trim()
}

// 检查是否存在明显的 SQL / 脚本注入攻击载荷
const containsDangerousPayload = (obj) => {
    for (const key in obj) {
        const val = obj[key]
        if (typeof val === 'string' && SQLI_REGEX.test(val)) {
            return true
        }
    }
    return false
}


// --- 表单数据状态 ---
const formData = reactive({
    name: '',
    email: '',
    whatsapp: '',
    order_type: 1,
    remark: ''
})

onMounted(async () => {
    const product = typeof route.query.product === 'string' ? route.query.product.trim() : ''
    if (!product) return
    formData.remark = t('contact.form.fromProduct', { name: product })
    await nextTick()
    const scrollToForm = () => {
        const form = document.getElementById('contact-form')
        if (!form) return
        const top = form.getBoundingClientRect().top + window.scrollY - 90
        window.scrollTo({ top, behavior: 'smooth' })
    }
    scrollToForm()
    const banner = document.querySelector('.contact-hero img')
    if (banner && !banner.complete) banner.addEventListener('load', scrollToForm, { once: true })
})

// 使用 computed 包装咨询类型下拉选项，并提供中英文映射
const inquiryTypes = computed(() => [
    { value: 1, label: t('contact.inquiry.general') },
    { value: 2, label: t('contact.inquiry.sales') },
    { value: 3, label: t('contact.inquiry.support') },
    { value: 4, label: t('contact.inquiry.hardware') },
])

const isSubmitting = ref(false)
const submitSuccess = ref(false)

// 表单提交逻辑
const submitForm = async () => {
    if (isSubmitting.value) return

    // 1. 蜜罐检测 (如果隐藏 input 有值，说明是被机器脚本批量填充)
    if (honeypot.value) {
        console.warn('Honeypot triggered')
        ElMessage.error(t('product.alert.invalidRequest'))
        resetSecurityState()
        return
    }

    const now = Date.now()
    if (now - lastSubmitTime.value < submitCooldown) {
        const waitSeconds = Math.ceil((submitCooldown - (now - lastSubmitTime.value)) / 1000)
        showMessage('warning', t('product.alert.notice'), t('product.alert.rateLimit', { seconds: waitSeconds }))
        return
    }

    const cleanName = sanitizeInput(formData.name)
    const cleanEmail = sanitizeInput(formData.email)
    const cleanWhatsapp = sanitizeInput(formData.whatsapp)
    const cleanRemark = sanitizeInput(formData.remark)

    // 邮箱与手机号格式严格校验（阻断在输入框塞入 SQL 语句）
    if (cleanEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.invalidEmail'))
        return
    }

    if (cleanWhatsapp && !/^[+0-9\s-]{6,20}$/.test(cleanWhatsapp)) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.invalidPhone'))
        return
    }

    // 4. 防御：恶意字符与 SQL 语法特征检测
    const sanitizedForm = {
        name: cleanName.slice(0, 50),
        email: cleanEmail.slice(0, 100),
        whatsapp: cleanWhatsapp.slice(0, 30),
        remark: cleanRemark.slice(0, 500),
        order_type: formData.order_type,
    }

    if (containsDangerousPayload(sanitizedForm)) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.dangerousContent'))
        return
    }

    // 2. 人机安全验证检查
    if (!turnstileToken.value) {
        ElMessage.warning(t('product.alert.securityCheck'))
        return
    }

    isSubmitting.value = true
    try {
        const postData = {
            ...sanitizedForm,
            turnstile_token: turnstileToken.value,
            honeypot: honeypot.value
        }
        await Order(postData)

        submitSuccess.value = true
        lastSubmitTime.value = now

        // 清空表单与重置安全状态
        formData.name = ''
        formData.email = ''
        formData.whatsapp = ''
        formData.order_type = 1
        formData.remark = ''
        resetSecurityState()

        // 3秒后隐藏成功提示
        setTimeout(() => {
            submitSuccess.value = false
        }, 3000)
    } catch (error) {
        console.error('Contact form submission failed:', error)
        resetSecurityState()
        ElMessage.error(error.message || t('product.alert.submitFailed'))
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
/* 原有的所有样式保持完全不变 */
/* 1. 父容器 Banner 设置为相对定位 */
.banner {
    position: relative;
    width: 100%;
    height: 500px;
    /* 假设是全屏 Banner */
    overflow: hidden;
}

/* 背景图处理同上 */
.background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
}

/* 2. **核心调整：使用绝对定位精确控制** */
.text-content {
    position: absolute;
    /* **关键调整：设置距离底部的距离（底部对齐）** */
    /* 调整这个值（例如 10%）可以控制文字的高低 */
    bottom: 40%;

    /* 水平居中方法 */
    left: 50%;
    transform: translateX(-50%);
    /* 水平偏移 50% 实现精确居中 */

    text-align: center;
    /* 文字内部居中 */
    color: white;
    z-index: 1;
    width: 90%;
    /* 给一个最大宽度，防止文字太多时贴边 */
}

/* 进一步精细化文字样式 */
.text-content h1 {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 1rem;
}

.text-content p {
    font-size: 1.2rem;
    font-weight: normal;
}

/* =========================================
   全局与基础样式
========================================= */
.contact-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #333;
    line-height: 1.6;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 80px 20px;
}

.bg-light {
    background-color: #f9fafb;
}

.grid-container {
    display: grid;
    gap: 30px;
}

.grid-3 {
    grid-template-columns: repeat(3, 1fr);
}

/* =========================================
   1. 顶部视觉 (Hero)
========================================= */


.hero-content {
    max-width: 700px;
    margin: 0 auto;
}

.hero-content h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    font-weight: 700;
}

.hero-content p {
    font-size: 1.1rem;
    color: #aaa;
}

/* =========================================
   2. 快捷联系方式 (Methods Grid)
========================================= */
.method-card {
    background: #fff;
    border: 1px solid #eaeaea;
    border-radius: 16px;
    padding: 40px 30px;
    text-align: center;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.method-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.05);
}

.method-icon {
    font-size: 2.5rem;
    margin-bottom: 20px;
}

.method-card h3 {
    font-size: 1.3rem;
    margin-bottom: 15px;
    color: #111;
}

.method-card p {
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 25px;
    min-height: 45px;
}

.contact-link {
    color: #007aff;
    text-decoration: none;
    font-weight: 600;
    font-size: 1rem;
}

.contact-link:hover {
    text-decoration: underline;
}

/* =========================================
   3. 表单与地址区 (Split Layout)
========================================= */
.split-layout {
    display: flex;
    gap: 80px;
}

.form-container {
    flex: 3;
}

.locations-container {
    flex: 2;
}

.form-container h2,
.locations-container h2 {
    font-size: 2rem;
    margin-bottom: 10px;
    color: #111;
}

.form-subtitle {
    color: #666;
    margin-bottom: 40px;
}

/* 表单样式 */
.contact-form {
    background: #fff;
    padding: 40px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    border: 1px solid #eaeaea;
}

.form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
}

.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.form-group label {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: #111;
}

.form-group input,
.form-group textarea,
.form-group select {
    padding: 12px 15px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.2s;
    outline: none;
    background-color: #fafafa;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
    border-color: #007aff;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

/* 下拉框自定义箭头包装器 */
.select-wrapper {
    position: relative;
}

.select-wrapper::after {
    content: '▼';
    font-size: 0.8rem;
    color: #999;
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.form-group select {
    width: 100%;
    appearance: none;
    cursor: pointer;
}

/* Turnstile 验证小部件容器 */
.form-turnstile {
    margin-bottom: 25px;
    display: flex;
    justify-content: flex-start;
}

/* 提交按钮 */
.btn-submit {
    background: #1a1a1a;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 15px 30px;
    font-size: 1.05rem;
    cursor: pointer;
    font-weight: 600;
    width: 100%;
    transition: all 0.3s ease;
    margin-top: 10px;
}

.btn-submit:hover:not(:disabled) {
    background: #333;
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.btn-submit:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.success-message {
    margin-top: 20px;
    padding: 15px;
    background-color: #e6f4ea;
    color: #137333;
    border-radius: 8px;
    text-align: center;
    font-weight: 500;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* 办公地址卡片 */
.locations-container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding-top: 85px;
}

.office-card {
    background: transparent;
    border-left: 3px solid #eaeaea;
    padding-left: 20px;
    transition: border-color 0.3s;
}

.office-card:hover {
    border-color: #007aff;
}

.office-card h4 {
    font-size: 1.1rem;
    margin-bottom: 10px;
    color: #111;
}

.office-card p {
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 5px;
    line-height: 1.5;
}

.office-phone {
    font-weight: 600;
    color: #333 !important;
    margin-top: 10px;
}

/* =========================================
   响应式适配
========================================= */
@media (max-width: 992px) {
    .split-layout {
        flex-direction: column;
        gap: 60px;
    }

    .locations-container {
        padding-top: 0;
    }

    .locations-container h2 {
        display: none;
        /* 手机端隐藏右侧重复的标题 */
    }

    .locations-container {
        flex-direction: row;
        flex-wrap: wrap;
    }

    .office-card {
        flex: 1;
        min-width: 250px;
    }
}

@media (max-width: 768px) {
    .grid-3 {
        grid-template-columns: 1fr;
    }

    .form-row {
        flex-direction: column;
        gap: 20px;
    }

    .contact-form {
        padding: 25px 20px;
    }

    .hero-content h1 {
        font-size: 2.2rem;
    }
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.modal-content {
    background: #fff;
    width: 90%;
    max-width: 550px;
    /* 稍微放宽一点展示图文 */
    border-radius: 20px;
    /* 圆角变大，更现代 */
    padding: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    /* 更柔和的高级阴影，去掉了丑陋的实体边框 */
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.5rem;
    color: #111;
    font-weight: 700;
}

/* 圆形灰色按钮，悬浮变深 */
.modal-header .close-btn {
    background: #f3f4f6;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.modal-header .close-btn:hover {
    background: #e5e7eb;
    color: #111;
}

.modal-body {
    flex: 1;
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 5px;
}

/* 滚动条美化 */
.modal-body::-webkit-scrollbar {
    width: 6px;
}

.modal-body::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
}

/* 弹窗图片容器 */
.modal-cover {
    width: 100%;
    height: 240px;
    background: #f9fafb;
    border-radius: 12px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
}

.modal-cover img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

/* 醒目的价格 */
.modal-price {
    font-size: 1.75rem;
    color: #ff4d4f;
    font-weight: 800;
    margin-bottom: 15px;
}

.modal-desc {
    font-size: 1rem;
    color: #4b5563;
    line-height: 1.7;
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    word-break: break-word;
    text-align: left;
}

.modal-footer {
    margin-top: 30px;
    display: flex;
    justify-content: flex-end;
    gap: 15px;
}

/* 按钮美化 */
.btn-cancel,
.btn-primary {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.btn-cancel {
    background: transparent;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.btn-cancel:hover {
    background: #f3f4f6;
    color: #111;
}

.btn-primary {
    background: #00bcd4;
    color: #fff;
}

.btn-primary:hover {
    background: #00acc1;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
}

/* 弹窗渐显动画 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active .modal-content,
.fade-leave-active .modal-content {
    transition: transform 0.3s ease cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from .modal-content,
.fade-leave-to .modal-content {
    transform: translateY(20px) scale(0.96);
}

/* =========================================
   系统提示弹窗 (Message Modal) 样式
========================================= */
.message-modal {
    max-width: 360px;
    /* 提示弹窗不需要太宽 */
    text-align: center;
    padding: 40px 30px;
    align-items: center;
}

.message-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.message-icon svg {
    width: 32px;
    height: 32px;
}

/* 成功状态配色：微弱的绿底 + 鲜艳的绿标 */
.message-icon.success {
    background-color: #d1fae5;
    color: #10b981;
}

/* 警告/必填状态配色：微弱的橙底 + 鲜艳的橙标 */
.message-icon.warning {
    background-color: #fef3c7;
    color: #f59e0b;
}

.message-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #111;
    margin: 0 0 10px 0;
}

.message-text {
    font-size: 1rem;
    color: #6b7280;
    margin: 0 0 30px 0;
    line-height: 1.5;
}

/* 让确定按钮撑满整行 */
.btn-block {
    width: 100%;
    padding: 14px 0;
    font-size: 1.05rem;
}
</style>